// netlify build's deploy context. can be "production", "deploy-preview" or "branch-deploy".
// if it's a preview env (including development), we'll also show Contentful entries in "Draft" status
export const IS_NETLIFY_PREVIEW = process.env.CONTEXT !== "production" || false;

// Values can be found here:
// https://docs.netlify.com/configure-builds/environment-variables/#build-metadata
export const IS_NETLIFY_DEV_SERVER = process.env.CONTEXT === "dev";

const LOCAL_TEST = process.env.LOCAL_TEST;
export const ACTIVE_ENV = LOCAL_TEST
  ? "development"
  : process.env.GATSBY_ACTIVE_ENV || process.env.NODE_ENV || "development";

export const IS_GATSBY_CLOUD = process.env.GATSBY_SERVER_ENV === "dev" || false;

export const IS_DEV_ENV =
  process.env.GATSBY_ACTIVE_ENV === "development" ||
  process.env.NODE_ENV === "development";

export const LOCALE = process.env.GATSBY_LOCALE || "en-US";

export const ALGOLIA_INDEX = process.env.ALGOLIA_INDEX;

export const GATSBY_RECAPTCHA_SITE_KEY = process.env.GATSBY_RECAPTCHA_SITE_KEY!;

export const GATSBY_SENTRY_ENV = process.env.GATSBY_SENTRY_ENV!;

export const GATSBY_SENTRY_DEV_DSN = process.env.GATSBY_SENTRY_DEV_DSN!;

export const GATSBY_SENTRY_PROD_DSN = process.env.GATSBY_SENTRY_PROD_DSN!;

export const GATSBY_DATADOG_APPLICATION_ID =
  process.env.GATSBY_DATADOG_APPLICATION_ID!;

export const GATSBY_DATADOG_CLIENT_TOKEN =
  process.env.GATSBY_DATADOG_CLIENT_TOKEN!;

export const NODE_ENV = process.env.NODE_ENV;

export const GTM_CONTAINER_ID = process.env.GATSBY_GTM_CONTAINER_ID!;

export const AMPLITUDE_API_KEY = process.env.GATSBY_AMPLITUDE_API_KEY!;

export const EXPERIMENT_DEPLOYMENT_KEY =
  process.env.GATSBY_AMPLITUDE_EXPERIMENT_KEY!;

export const GATSBY_SERVER_ENV = process.env.GATSBY_SERVER_ENV!;

export const IS_PROD = GATSBY_SERVER_ENV === "prod";

export const LOCALE_TO_DOMAIN: Record<string, string> = {
  "en-US": "https://www.flexport.com",
  "zh-CN": "https://cn.flexport.com",
};

/**
 * sanitize-html configuration.
 */
export const SANITIZATION_OPTIONS = {
  allowedTags: [
    "a",
    "blockquote",
    "br",
    "div",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "iframe",
    "img",
    "li",
    "ol",
    "p",
    "section",
    "span",
    "table",
    "tbody",
    "td",
    "th",
    "thead",
    "tr",
    "ul",
  ],
  alwaysAllowedAttributes: ["class", "target"],
  allowedAttributesPerTag: {
    img: ["src", "alt"],
    iframe: ["src", "width", "height", "style"],
    a: ["data-*", "href", "rel"],
    span: ["style"],
    td: ["style"],
    th: ["style"],
    tr: ["style"],
  },
  allowedCSSPropertiesPerTag: {
    // No CSS filtering by default - tags can use any CSS properties
    // Override this in SANITIZATION_OPTIONS_WITH_RESTRICTED_STYLES for security
  } as Record<string, string[]>,
  get allowedAttributes(): Record<string, string[]> {
    return this.allowedTags.reduce(
      (obj, tag) => ({
        ...obj,
        [tag]: this.alwaysAllowedAttributes.concat(
          this.allowedAttributesPerTag[
            tag as keyof typeof this.allowedAttributesPerTag
          ] || []
        ),
      }),
      {} as Record<string, string[]>
    );
  },
  allowedIframeHostnames: [
    "www.youtube.com",
    "fast.wistia.net",
    "public.tableau.com",
  ],
  allowedIframeDomains: ["ctfassets.net", "hackerone.com", "tech.flexport.com"],
};

/**
 * Creates a CSS style validator based on allowedCSSPropertiesPerTag configuration
 */
function createStyleAttributeTransform(
  allowedCSSPropertiesPerTag: Record<string, string[]>
) {
  return (
    tagName: string,
    attribs: Record<string, string>
  ): {tagName: string; attribs: Record<string, string>} => {
    if (attribs.style) {
      const allowedProperties = allowedCSSPropertiesPerTag[tagName];

      // If no CSS properties are specified for this tag, remove all styles
      if (!allowedProperties || allowedProperties.length === 0) {
        delete attribs.style;
        return {tagName, attribs};
      }

      // Parse and filter the style string
      const styles = attribs.style
        .split(";")
        .map(style => style.trim())
        .filter(Boolean)
        .map(style => {
          const [property, ...valueParts] = style.split(":");
          if (!property || valueParts.length === 0) return null;
          return {
            property: property.trim().toLowerCase(),
            value: valueParts.join(":").trim(),
          };
        })
        .filter(Boolean) as Array<{property: string; value: string}>;

      // Filter to only allowed properties for this specific tag
      const filteredStyles = styles.filter(style =>
        allowedProperties.includes(style.property)
      );

      // Rebuild the style string
      attribs.style = filteredStyles
        .map(style => `${style.property}: ${style.value}`)
        .join("; ");

      if (!attribs.style) {
        delete attribs.style;
      }
    }
    return {tagName, attribs};
  };
}

/**
 * Sanitization options with restricted inline styles support.
 * Restricts CSS properties for tags that support styles.
 */
export const SANITIZATION_OPTIONS_WITH_RESTRICTED_STYLES = (() => {
  // Define which CSS properties are allowed for each tag that supports styles
  const allowedCSSPropertiesPerTag: Record<string, string[]> = {
    span: ["color", "font-size"],
    td: ["vertical-align"],
    th: ["vertical-align"],
    tr: ["vertical-align"],
  };

  // Find all tags that have "style" in their allowedAttributesPerTag
  const tagsWithStyle = Object.keys(
    SANITIZATION_OPTIONS.allowedAttributesPerTag
  ).filter(tag => {
    const attrs =
      SANITIZATION_OPTIONS.allowedAttributesPerTag[
        tag as keyof typeof SANITIZATION_OPTIONS.allowedAttributesPerTag
      ];
    return Array.isArray(attrs) && attrs.includes("style");
  });

  // Create the transform function with our CSS restrictions
  const styleAttributeTransform = createStyleAttributeTransform(
    allowedCSSPropertiesPerTag
  );

  // Build transformTags object for all tags that support style
  const transformTags = tagsWithStyle.reduce(
    (acc, tag) => ({
      ...acc,
      [tag]: styleAttributeTransform,
    }),
    {} as Record<string, typeof styleAttributeTransform>
  );

  return {
    ...SANITIZATION_OPTIONS,
    allowedCSSPropertiesPerTag,
    transformTags,
  };
})();

// tracking tags configuration
/**
 * generate google tag tracking script
 * @param {string} containerId
 * @return {string}
 */
const googleTagString = (containerId: string) =>
  `
  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl;var n=d.querySelector('[nonce]');
  n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));w.fpGTMCustomScriptsNonce=n.nonce||n.getAttribute('nonce');
  f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','${containerId || ""}');
  `;

export const TRACKING_CONFIG = {
  GTM_DEFAULT: {
    id: "fp-third-party-gtm",
    script: googleTagString(GTM_CONTAINER_ID!),
    nonce: true,
  },
};

/**
 * Ketch override tags
 * This script defeats the nonce's purpose
 * this should be destroyed after use
 * @return {string}
 */
export const KETCH_NONCE_SCRIPT = `
(function () {
  const originalCreate = Document.prototype.createElement;
  const firstNonceEl = document.querySelector('script[nonce]');
  const nonce = firstNonceEl && (firstNonceEl.nonce || firstNonceEl.getAttribute('nonce'));
  if (!nonce) return;
  Document.prototype.createElement = function (tag) {
    const el = originalCreate.call(this, tag);
    if (tag.toLowerCase() === 'script') {
      el.setAttribute('nonce', nonce);
    }
    return el;
  };
})();
`;
